import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PriceTag from '../components/PriceTag';
import SizeChart from '../components/SizeChart';
import { whatsappUrl } from '../data/whatsapp';
import { GOLD, GOLD_LT, CREAM, INK } from '../theme';

const FIXED_SIZES = ['XS', 'S', 'M', 'L', 'XL'];

// Unstitched and Sizes are the only two real price tiers (mirrors
// akbaraslam.com: every fixed size + Custom Size share one "sized" price,
// only Unstitched differs) — so all six sized options just relabel the same
// `sizedPrice`, they aren't independently priced.
function buildOptions(dress) {
  const options = [];
  if (dress.unstitchedPrice != null) {
    options.push({ key: 'unstitched', label: 'Unstitched', price: dress.unstitchedPrice });
  }
  if (dress.sizedPrice != null) {
    for (const size of FIXED_SIZES) options.push({ key: size, label: size, price: dress.sizedPrice });
    options.push({ key: 'custom', label: 'Custom Size', price: dress.sizedPrice });
  }
  return options;
}

const enquireStyle = {
  display: 'inline-block',
  fontSize: 13,
  letterSpacing: '.16em',
  textTransform: 'uppercase',
  color: GOLD_LT,
  border: `1px solid ${GOLD_LT}`,
  padding: '10px 20px',
  textDecoration: 'none',
};

const bookStyle = {
  display: 'inline-block',
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  padding: '15px 30px',
  background: GOLD,
  color: INK,
  textDecoration: 'none',
};

const whatsappStyle = {
  display: 'inline-block',
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  padding: '15px 30px',
  background: 'none',
  border: `1px solid ${GOLD_LT}`,
  color: GOLD_LT,
  textDecoration: 'none',
};

// 9px vertical padding on 13px text gave a ~35px target — under the 44px
// minimum, on the one control every buyer has to touch.
const pillStyle = (active) => ({
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: 14,
  letterSpacing: '.06em',
  padding: '12px 18px',
  minHeight: 44,
  border: `1px solid ${active ? GOLD : 'rgba(201,162,75,.4)'}`,
  background: active ? GOLD : 'none',
  color: active ? INK : CREAM,
  cursor: 'pointer',
});

const groupLabelStyle = { fontSize: 13, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT };

// Every dress — priced or not — leads to Book an Appointment + WhatsApp; no
// cart/checkout exists yet. Price, when set, is display-only. `compact` is
// the in-grid-card variant: just a price OR an "Enquire" link to the dress
// page, no CTA buttons (those live in the full view / Quick View).
export default function DressCTA({ dress, categorySlug, compact }) {
  const options = buildOptions(dress);
  const hasUnstitched = dress.unstitchedPrice != null;
  const hasSized = dress.sizedPrice != null;

  // Default to the stitched path when both exist: it is the made-to-measure
  // service the atelier actually sells, and it is what the sizes below
  // belong to. Unstitched stays one tap away.
  const [finish, setFinish] = useState(hasSized ? 'stitched' : 'unstitched');
  const [selectedKey, setSelectedKey] = useState(hasSized ? FIXED_SIZES[0] : 'unstitched');

  useEffect(() => {
    const nextFinish = dress.sizedPrice != null ? 'stitched' : 'unstitched';
    setFinish(nextFinish);
    setSelectedKey(nextFinish === 'stitched' ? FIXED_SIZES[0] : 'unstitched');
  }, [dress.slug, dress.sizedPrice]);

  const sizeOptions = options.filter((o) => o.key !== 'unstitched');
  const showSizes = hasSized && (finish === 'stitched' || !hasUnstitched);
  const selected = options.find((o) => o.key === selectedKey) || options[0] || null;
  const detailHref = `/categories/${categorySlug}/${dress.slug}`;
  const enquiryName = selected ? `${dress.name} (${selected.label})` : dress.name;
  const bookHref = `/book-appointment?dress=${encodeURIComponent(enquiryName)}&categorySlug=${encodeURIComponent(categorySlug)}&dressSlug=${encodeURIComponent(dress.slug)}`;

  if (compact) {
    if (options.length === 0) return <Link to={detailHref} style={enquireStyle}>Enquire</Link>;
    const min = Math.min(...options.map((o) => o.price));
    const showFrom = dress.unstitchedPrice != null && dress.sizedPrice != null;
    return showFrom ? (
      <span style={{ fontSize: 18, color: CREAM, whiteSpace: 'nowrap' }}>From <PriceTag price={min} style={{ fontSize: 18 }} /></span>
    ) : (
      <PriceTag price={min} />
    );
  }

  const pageUrl = `${window.location.origin}${detailHref}`;

  return (
    <div>
      {options.length > 0 && (
        <div style={{ marginBottom: 22, maxWidth: 460 }}>
          {options.length > 1 && (
            <>
              {/* "Unstitched" is a finish, not a body measurement, but it used
                  to sit in the same row as XS–XL — so the control asked one
                  question and offered answers to two, with the odd one out
                  preselected and highlighted. Splitting them also cuts the
                  first decision from seven options to two. */}
              {hasUnstitched && hasSized && (
                <div style={{ marginBottom: 20 }}>
                  <div id="ij-finish-label" style={{ ...groupLabelStyle, marginBottom: 12 }}>Finish</div>
                  <div role="group" aria-labelledby="ij-finish-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {[['unstitched', 'Unstitched'], ['stitched', 'Stitched']].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={finish === key}
                        onClick={() => {
                          setFinish(key);
                          setSelectedKey(key === 'unstitched' ? 'unstitched' : FIXED_SIZES[0]);
                        }}
                        style={pillStyle(finish === key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {showSizes && (
                <div style={{ marginBottom: 16 }}>
                  {/* The chart link now sits on the same row as the label it
                      explains. It used to be flushed to the far right of a
                      column that runs most of the page width, ending up
                      hundreds of pixels from the sizes it describes. */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                    <span id="ij-size-label" style={groupLabelStyle}>Size</span>
                    <SizeChart />
                  </div>
                  <div role="group" aria-labelledby="ij-size-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {sizeOptions.map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        aria-pressed={o.key === selectedKey}
                        onClick={() => setSelectedKey(o.key)}
                        style={pillStyle(o.key === selectedKey)}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <PriceTag price={selected.price} style={{ display: 'block', fontSize: 26 }} />
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Link to={bookHref} style={bookStyle}>Book an Appointment</Link>
        <a href={whatsappUrl(enquiryName, pageUrl)} target="_blank" rel="noopener noreferrer" style={whatsappStyle}>Talk to Fashion Consultant</a>
      </div>
    </div>
  );
}
